/**
 * MyUnpublishedApps — the two unpublished-AI sections of My Page:
 *
 *   1. My Unpublished AI Applications      — apps the user owns (app_owner === me)
 *      that are awaiting the user's action. Same row markup, edit form, and
 *      discard flow as the Application Repository "Unpublished" tab.
 *   2. My Team's Unpublished AI Applications — unpublished apps owned by the
 *      user's direct/indirect reports, grouped by owner (expand to see rows).
 *
 * Ownership is resolved with the same cached supervisor-chain technique used by
 * the published/"View as" logic: one lookup per distinct owner alias.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Container,
  Header,
  Pagination,
  SpaceBetween,
  Spinner,
  Icon,
  Modal,
  Button,
  FormField,
  Input,
} from "@amzn/awsui-components-react";

import "./homepage.scss";

import SkeletonCard from "./SkeletonCard";
import AutomatedAppEditForm from "./AutomatedAppEditForm";
import NonProdPeopleApiWarning from "./NonProdPeopleApiWarning";
import { TEMPLATES } from "src/Ateam-BI/Templates/Constants";
import { formatRunCount } from "src/generic-components/appCardUtils";
import {
  getAutomatedAppsByStatus,
  updateAutomatedApp,
} from "src/utilities/AutomatedAppsAPIs";
import {
  groupUnpublishedByOwner,
  getUnpublishedAppOwner,
  getEmployeeInfo,
  getReportRelationship,
  mapWithConcurrency,
  PAPI_LOOKUP_CONCURRENCY,
} from "../Ateam-BI/Templates/Utils/MyApplicationsUtils";
import { getLocalCache, setLocalCache } from "src/utilities/CacheHelper";
import { MY_UNPUBLISHED_CACHE_KEY, cacheVariables } from "src/AppConstants/CacheConstants";

// One card per row in both sections, so page size == rows per page.
const PAGE_SIZE = 2; // My Unpublished AI Applications: 2 rows per page
const TEAM_PAGE_SIZE = 4; // My Team's Unpublished AI Applications: 4 rows per page

// Per-user cache key so one user's My Page data never leaks to another.
const cacheKeyFor = (user) => `${MY_UNPUBLISHED_CACHE_KEY}:${(user || "").toLowerCase()}`;

const appKey = (item) => `${item.resource_type}#${item.resource_id}`;

function MyUnpublishedApps() {
  const userId = useSelector((globalState) => globalState.auth.userId);

  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [mineApps, setMineApps] = useState([]);
  const [teamGroups, setTeamGroups] = useState([]); // [{ owner, apps }]

  const [editingApp, setEditingApp] = useState(null);
  const [archiveConfirmApp, setArchiveConfirmApp] = useState(null);
  const [discardReason, setDiscardReason] = useState("");

  const [minePageIndex, setMinePageIndex] = useState(1);
  const [teamPageIndex, setTeamPageIndex] = useState(1);
  const [expandedOwners, setExpandedOwners] = useState({});

  // ---- Load + partition ----------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    if (!userId) return undefined;

    // The cache stores ONLY the expensive-to-compute owner enrichment
    // (owner -> { name, businessTitle, relationship }) — a small map of
    // aliases/names, never the app objects. App objects are always fetched
    // fresh from getAutomatedAppsByStatus (a fast DynamoDB query, not the
    // bottleneck) and re-hydrated against this map. This keeps the cache tiny
    // (well under the localStorage quota) while still skipping the slow PAPI
    // supervisor-chain resolution on repeat visits.
    const cacheConf = cacheVariables[MY_UNPUBLISHED_CACHE_KEY];
    const cachedEnrichment =
      cacheConf?.useCache
        ? getLocalCache(cacheKeyFor(userId), cacheConf.expiry)
        : null;

    setLoading(true);
    setResolving(false);

    // Build team groups from a fetched app list + a resolved owner-enrichment
    // map (owner -> {name, businessTitle, relationship}). Only owners present
    // in the map (i.e. the user's direct/indirect reports) become groups.
    const buildTeamGroups = (aiItems, me, enrichmentByOwner) => {
      const nonSelf = aiItems.filter(
        (app) => getUnpublishedAppOwner(app).toLowerCase() !== me
      );
      return groupUnpublishedByOwner(nonSelf)
        .filter((group) => enrichmentByOwner[group.owner.toLowerCase()])
        .map((group) => {
          const e = enrichmentByOwner[group.owner.toLowerCase()];
          return { ...group, name: e.name, businessTitle: e.businessTitle, relationship: e.relationship };
        })
        .sort((a, b) => b.apps.length - a.apps.length);
    };

    const onSuccess = async (result) => {
      // getAutomatedAppsByStatus streams intermediate callbacks (loading=true)
      // as it pages through results, then a final one (loading=false) with the
      // complete list. Only partition/enrich on the final callback — running it
      // on partial pages caused overlapping async runs whose out-of-order state
      // updates could leave a partial team list on screen.
      if (result?.loading) return;

      const raw = result?.data?.getAutomatedAppsByStatus;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const items = parsed?.items || parsed?.Items || parsed || [];
      // Only AI apps are unpublished/automated, but guard anyway.
      const aiItems = items.filter((i) => i.template_id === TEMPLATES.AI);

      if (cancelled) return;

      // "Mine" is purely app_owner === me — compute it synchronously and commit
      // it together with clearing the loader, so the empty state never flashes
      // before the apps render. Only the team section needs async chain lookups.
      const me = (userId || "").toLowerCase();
      const mine = aiItems.filter(
        (app) => getUnpublishedAppOwner(app).toLowerCase() === me
      );
      setMineApps(mine);
      setLoading(false);

      // Cache HIT: build team groups from the fresh apps + cached enrichment —
      // no PAPI lookups needed.
      if (cachedEnrichment) {
        setTeamGroups(buildTeamGroups(aiItems, me, cachedEnrichment));
        setResolving(false);
        return;
      }

      // Cache MISS: resolve owners' relationships/names via PAPI (streamed).
      setTeamGroups([]);
      setResolving(true);

      // Candidate owners = everyone who owns an unpublished app and isn't me.
      const candidateGroups = groupUnpublishedByOwner(
        aiItems.filter((app) => getUnpublishedAppOwner(app).toLowerCase() !== me)
      );

      // Accumulate only the lightweight enrichment (aliases/names), to cache.
      const enrichmentByOwner = {};
      let streamFailed = false;

      try {
        const cache = {};
        await mapWithConcurrency(
          candidateGroups,
          async (group) => {
            const relationship = await getReportRelationship(group.owner, userId, cache);
            if (!relationship || cancelled) return; // owner is not my report
            const info = await getEmployeeInfo(group.owner, cache);
            if (cancelled) return;
            enrichmentByOwner[group.owner.toLowerCase()] = {
              name: info.name,
              businessTitle: info.businessTitle,
              relationship,
            };
            const enrichedGroup = {
              ...group,
              name: info.name,
              businessTitle: info.businessTitle,
              relationship,
            };
            // Append and keep sorted by unpublished-count (desc), like before.
            setTeamGroups((prev) =>
              [...prev, enrichedGroup].sort((a, b) => b.apps.length - a.apps.length)
            );
          },
          PAPI_LOOKUP_CONCURRENCY
        );
      } catch (error) {
        // "mine" is already committed above; only the team section failed here.
        streamFailed = true;
        console.error("MyUnpublishedApps: failed to resolve team apps", error);
      } finally {
        if (!cancelled) setResolving(false);
      }

      // Cache only the tiny enrichment map (owner -> name/title/relationship),
      // never app objects. Skip on a real failure so we don't persist a partial
      // map, and skip on cancellation — a cancelled run bails out of the
      // per-owner loop early, leaving `enrichmentByOwner` incomplete.
      if (!streamFailed && !cancelled && cacheConf?.useCache) {
        setLocalCache(cacheKeyFor(userId), enrichmentByOwner);
      }
    };

    const onFailure = (error) => {
      console.error("MyUnpublishedApps: failed to fetch unpublished apps", error);
      if (!cancelled) {
        setLoading(false);
        setResolving(false);
        setMineApps([]);
        setTeamGroups([]);
      }
    };

    getAutomatedAppsByStatus("UNPUBLISHED", onSuccess, onFailure);

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ---- Discard / publish ---------------------------------------------------
  const removeAppFromState = useCallback((item) => {
    setMineApps((prev) => prev.filter((a) => appKey(a) !== appKey(item)));
    setTeamGroups((prev) =>
      prev
        .map((g) => ({ ...g, apps: g.apps.filter((a) => appKey(a) !== appKey(item)) }))
        .filter((g) => g.apps.length > 0)
    );
    // Invalidate the cached snapshot so a discarded/published app doesn't
    // reappear from cache on the next visit.
    try {
      localStorage.removeItem(cacheKeyFor(userId));
    } catch (e) {
      /* ignore storage errors */
    }
  }, [userId]);

  const confirmDiscard = useCallback(() => {
    if (!archiveConfirmApp) return;
    updateAutomatedApp(
      { ...archiveConfirmApp, status: "ARCHIVED", archival_reason: discardReason.trim() },
      () => {
        removeAppFromState(archiveConfirmApp);
        setArchiveConfirmApp(null);
        setDiscardReason("");
      },
      () => {
        setArchiveConfirmApp(null);
        setDiscardReason("");
      }
    );
  }, [archiveConfirmApp, discardReason, removeAppFromState]);

  // ---- Derived pagination --------------------------------------------------
  const minePagesCount = Math.max(1, Math.ceil(mineApps.length / PAGE_SIZE));
  const teamPagesCount = Math.max(1, Math.ceil(teamGroups.length / TEAM_PAGE_SIZE));

  useEffect(() => {
    if (minePageIndex > minePagesCount) setMinePageIndex(minePagesCount);
  }, [minePageIndex, minePagesCount]);
  useEffect(() => {
    if (teamPageIndex > teamPagesCount) setTeamPageIndex(teamPagesCount);
  }, [teamPageIndex, teamPagesCount]);

  const minePageItems = useMemo(
    () => mineApps.slice((minePageIndex - 1) * PAGE_SIZE, minePageIndex * PAGE_SIZE),
    [mineApps, minePageIndex]
  );
  const teamPageItems = useMemo(
    () =>
      teamGroups.slice(
        (teamPageIndex - 1) * TEAM_PAGE_SIZE,
        teamPageIndex * TEAM_PAGE_SIZE
      ),
    [teamGroups, teamPageIndex]
  );

  const teamAppCount = useMemo(
    () => teamGroups.reduce((sum, g) => sum + g.apps.length, 0),
    [teamGroups]
  );

  const toggleOwner = (owner) =>
    setExpandedOwners((prev) => ({ ...prev, [owner]: !prev[owner] }));

  const initials = (nameOrAlias) => {
    const parts = (nameOrAlias || "?").trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (nameOrAlias || "?").slice(0, 2).toUpperCase();
  };

  // Status pill shown at the top-right of each row (matches the mock).
  const renderStatusBadge = (item) => {
    const isDraft = (item?.review_status || "").toUpperCase() === "DRAFT";
    if (isDraft) {
      return (
        <span style={{ border: "1px solid #b6bec9", color: "#5f6b7a", padding: "3px 11px", borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}>Draft</span>
      );
    }
    return (
      <span style={{ backgroundColor: "#fef3e8", color: "#d4760a", padding: "4px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}>Pending Review</span>
    );
  };

  // ---- Row renderers -------------------------------------------------------
  // `readOnly` rows (team section) have no edit/publish or discard actions and
  // are not clickable — they are informational only.
  const renderAppRow = (item, readOnly = false) => (
    <div
      key={appKey(item)}
      className={`unpublished-card-item${readOnly ? " unpublished-card-item--readonly" : ""}`}
      onClick={readOnly ? undefined : () => setEditingApp(item)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "16px", fontWeight: 700 }}>{item.app_name}</span>
          {item.template_id === TEMPLATES.AI && (
            <span className="ai-powered-badge"><Icon name="gen-ai" /> AI-Powered</span>
          )}
        </div>
        {renderStatusBadge(item)}
      </div>
      <Box color="text-body-secondary" margin={{ bottom: "xs" }}>{item.app_description}</Box>
      <Box fontSize="body-s" margin={{ bottom: "xs" }}>
        <SpaceBetween direction="horizontal" size="l">
          <span><span style={{ color: "#5f6b7a" }}>Created:</span> {item.created_at}</span>
          <span><span style={{ color: "#5f6b7a" }}>Last Modified:</span> {item.last_updated_at}</span>
          <span><span style={{ color: "#5f6b7a" }}>Created By:</span> {item.created_by}</span>
          <span><span style={{ color: "#5f6b7a" }}>App Builder:</span> {item.app_owner}</span>
          {item.template_id === TEMPLATES.AI && formatRunCount(item.run_count) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <span>▶</span>
              <span style={{ color: "#5f6b7a" }}>{formatRunCount(item.run_count)} runs</span>
            </span>
          )}
        </SpaceBetween>
      </Box>
      {!readOnly && (
        <SpaceBetween direction="horizontal" size="l">
          <span className="edit-publish-link">Click to edit and publish →</span>
          <span
            className="discard-link"
            onClick={(e) => { e.stopPropagation(); setArchiveConfirmApp(item); }}
          >
            Discard
          </span>
        </SpaceBetween>
      )}
    </div>
  );

  const renderRelationshipBadge = (relationship) => {
    if (!relationship) return null;
    if (relationship.type === "Direct") {
      return (
        <span style={{ background: "#e9f0fb", color: "#0972d3", borderRadius: "12px", padding: "1px 9px", fontSize: "12px", fontWeight: 600 }}>
          Direct
        </span>
      );
    }
    return (
      <span style={{ background: "#f1f3f5", color: "#5f6b7a", borderRadius: "12px", padding: "1px 9px", fontSize: "12px", fontWeight: 600 }}>
        via {relationship.viaName || relationship.via}
      </span>
    );
  };

  const renderTeamMember = ({ owner, apps, name, businessTitle, relationship }) => {
    const expanded = !!expandedOwners[owner];
    const displayName = name || owner;
    return (
      <div key={owner} className="team-member-row" style={{ borderBottom: "1px solid #e9ebed" }}>
        <div
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 4px", cursor: "pointer" }}
          onClick={() => toggleOwner(owner)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px", height: "36px", borderRadius: "50%",
                background: "#0972d3", color: "#fff", display: "flex",
                alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700,
              }}
            >
              {initials(displayName)}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: 700 }}>{displayName}</span>
                {renderRelationshipBadge(relationship)}
              </div>
              {businessTitle && (
                <div style={{ color: "#5f6b7a", fontSize: "13px" }}>{businessTitle}</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ color: "#d4760a", border: "1px solid #f0c38a", borderRadius: "16px", padding: "2px 10px", fontSize: "13px" }}>
              {apps.length} unpublished
            </span>
            <Icon name={expanded ? "caret-up-filled" : "caret-down-filled"} />
          </div>
        </div>
        {expanded && (
          <Box padding={{ bottom: "s" }}>
            <div className="unpublished-cards-container">
              {apps.map((app) => renderAppRow(app, true))}
            </div>
          </Box>
        )}
      </div>
    );
  };

  // ---- Edit form takes over the section while active -----------------------
  if (editingApp) {
    return (
      <Box padding={{ top: "l" }}>
        <AutomatedAppEditForm
          item={editingApp}
          userId={userId}
          onSave={(updatedItem) => {
            if (updatedItem?.status && updatedItem.status !== "UNPUBLISHED") {
              // App left the unpublished list (e.g. published) — drop it.
              removeAppFromState(editingApp);
            } else if (updatedItem) {
              // Still UNPUBLISHED: replace the row in state so the edited
              // name/description/etc. show immediately instead of the stale
              // pre-edit values (which persisted until a full reload).
              setMineApps((prev) =>
                prev.map((a) => (appKey(a) === appKey(updatedItem) ? updatedItem : a))
              );
            }
            setEditingApp(null);
          }}
          onCancel={() => setEditingApp(null)}
        />
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* Section 1: My unpublished */}
      <Container
        header={
          <Header
            variant="h2"
            counter={!loading ? `(${mineApps.length})` : undefined}
            description="Unpublished AI applications awaiting your action"
          >
            My Unpublished AI Applications
          </Header>
        }
      >
        {loading ? (
          <Box padding={{ top: "l" }}><SkeletonCard count={2} /></Box>
        ) : mineApps.length === 0 ? (
          <Box textAlign="center" color="inherit" padding={{ top: "l" }}>
            <b>No unpublished applications</b>
            <Box variant="p" color="inherit">You have no unpublished apps awaiting review.</Box>
          </Box>
        ) : (
          <SpaceBetween size="m">
            <div className="unpublished-cards-container">
              {minePageItems.map((item) => renderAppRow(item))}
            </div>
            {minePagesCount > 1 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  currentPageIndex={minePageIndex}
                  pagesCount={minePagesCount}
                  onChange={({ detail }) => setMinePageIndex(detail.currentPageIndex)}
                  openEnd={false}
                />
              </div>
            )}
          </SpaceBetween>
        )}
      </Container>

      {/* Section 2: My team's unpublished — streams in as owners resolve */}
      <NonProdPeopleApiWarning>
        People APIs data in lower environments is not up to date, so the
        reporting-relationship resolution behind{" "}
        <strong>My Team's Unpublished AI Applications</strong> (grouping apps by
        your direct/indirect reports) may be inaccurate. This list may not
        reflect production data.
      </NonProdPeopleApiWarning>

      <Container
        header={
          <Header
            variant="h2"
            counter={teamGroups.length > 0 ? `(${teamAppCount})` : undefined}
            description="Unpublished AI applications across your direct and indirect reports"
          >
            My Team's Unpublished AI Applications
          </Header>
        }
      >
        {(loading || resolving) && teamGroups.length === 0 ? (
          // Nothing resolved yet — full-body loading indicator.
          <Box textAlign="center" color="text-body-secondary" padding={{ top: "l" }}>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Spinner size="normal" />
              <span>Resolving your team's applications…</span>
            </SpaceBetween>
          </Box>
        ) : teamGroups.length === 0 ? (
          <Box textAlign="center" color="inherit" padding={{ top: "l" }}>
            <b>No team applications</b>
            <Box variant="p" color="inherit">None of your reports have unpublished apps.</Box>
          </Box>
        ) : (
          <SpaceBetween size="m">
            <div>{teamPageItems.map((group) => renderTeamMember(group))}</div>
            {resolving && (
              <Box textAlign="center" color="text-body-secondary">
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <Spinner size="normal" />
                  <span>Loading more team members…</span>
                </SpaceBetween>
              </Box>
            )}
            {teamPagesCount > 1 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  currentPageIndex={teamPageIndex}
                  pagesCount={teamPagesCount}
                  onChange={({ detail }) => setTeamPageIndex(detail.currentPageIndex)}
                  openEnd={false}
                />
              </div>
            )}
          </SpaceBetween>
        )}
      </Container>

      {/* Discard confirmation */}
      {archiveConfirmApp && (
        <Modal
          visible={!!archiveConfirmApp}
          onDismiss={() => { setArchiveConfirmApp(null); setDiscardReason(""); }}
          header="Confirm Discard"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => { setArchiveConfirmApp(null); setDiscardReason(""); }}>Cancel</Button>
                <Button variant="primary" disabled={!discardReason.trim()} onClick={confirmDiscard}>Discard</Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>Are you sure you want to discard <Box variant="strong">{archiveConfirmApp?.app_name}</Box>?</Box>
            <Box>This will discard the application and remove it from the unpublished list.</Box>
            <FormField label="Reason for discarding" constraintText="Required">
              <Input
                value={discardReason}
                placeholder="Provide reason for discarding this application"
                onChange={({ detail }) => setDiscardReason(detail.value)}
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </SpaceBetween>
  );
}

export default MyUnpublishedApps;
